import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Paper,
  Divider,
  Stack,
  IconButton,
  InputAdornment,
  Avatar,
  Badge,
} from "@mui/material";
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Clear as ClearIcon,
  ShoppingCart as CartIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { useState } from "react";
import axios from "axios";
import { baseUrl, config, requestID, getBusinessData, getUser } from "../../../common/utils";
import { formattedDate } from "../../../common/utils_t";
import { MenuButton } from "../../../components/Menu";

type ReversedOrder = {
  id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  reversed_code: string;
  total_amount: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

type OrderItem = {
  id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price: string;
  total_amount: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

type Order = {
  id: number;
  business_id: number;
  branch_id: number;
  total_price: string;
  order_code: string;
  order_date: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  balance_amount: number | null;
  order_id: string;
  mode_of_payment: string;
  payable_amount: string;
  status: string;
  active_status: string;
  order_items: OrderItem[];
  reversed_code: ReversedOrder;
};

const ReversedOrder: React.FC = () => {
  const businessData = getUser()?.businessData;
  const [searchQuery, setSearchQuery] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setOrders([]);
      return;
    }

    const data = {
      order_code: query,
      requestID: requestID(),
      business_id: businessData.business_id
    };

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        baseUrl() + "orderhistorys/searchOrdersByOrderCode",
        data,
        config
      );
      const result = response.data;
      console.log({ response });

      if (result.status) {
        setOrders(result.data || []);
      } else {
        setErrorMessage(result.message || "No orders found");
        setOrders([]);
      }
    } catch (error: any) {
      console.error("Search error:", error);
      setErrorMessage("Failed to search orders. Please try again.");
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReverseOrder = async () => {
    if (!selectedOrder) return;

    const data = {
      order_id: selectedOrder.order_id,
      requestID: requestID(),
    };

    setIsLoading(true);

    try {
      const response = await axios.post(
        baseUrl() + "Orderhistorys/reverseOrder",
        data,
        config
      );
      const result = response.data;
      console.log({ response });

      if (result.status) {
        setSnackbarMessage("Order cancelled successfully!");
        setSnackbarSeverity("success");
        setShowSnackbar(true);
        handleSearch(searchQuery);
      } else {
        setSnackbarMessage(result.message || "Failed to cancel order");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    } catch (error: any) {
      console.error("Reverse order error:", error);
      setSnackbarMessage("Failed to cancel order. Please try again.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
      setShowDialog(false);
      setSelectedOrder(null);
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const confirmCancelOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowDialog(true);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setOrders([]);
    setErrorMessage("");
  };

  const onsearchHandler = (e: any) => {
    const query = e.target.value;
    setSearchQuery(query);
    const timeoutId = setTimeout(() => handleSearch(query), 1000);
    return () => clearTimeout(timeoutId);
  };

  return (
    <IonPage className="pushDown  ">
      <IonHeader className=" ">
        <IonToolbar> <MenuButton/>
          <IonButtons slot="start">
            {/* <IonBackButton defaultHref="/dashboard" /> */}
          </IonButtons>
          <IonTitle>Order Management</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3, bgcolor: '#fafafa', minHeight: '100vh' }}>
          {/* Modern Search Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 1, 
                color: '#1a1a1a',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Search Orders
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: 3,
                fontSize: '1rem'
              }}
            >
              Find and manage your orders quickly
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 0,
                border: '2px solid #e8e8e8',
                borderRadius: '16px',
                transition: 'all 0.2s ease',
                '&:focus-within': {
                  borderColor: '#1976d2',
                  boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
                }
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter order number to search..."
                value={searchQuery}
                onChange={(e) => onsearchHandler(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999', fontSize: '1.25rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={clearSearch} 
                        size="small"
                        sx={{ 
                          bgcolor: '#f5f5f5',
                          '&:hover': { bgcolor: '#e0e0e0' }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    border: 'none',
                    fontSize: '1rem',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 0',
                  }
                }}
              />
            </Paper>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              py={8}
              sx={{
                bgcolor: 'white',
                borderRadius: '20px',
                border: '1px solid #e8e8e8'
              }}
            >
              <CircularProgress size={48} thickness={4} sx={{ mb: 3 }} />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                Searching orders...
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Please wait while we find your orders
              </Typography>
            </Box>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: '16px',
                border: '1px solid #ffebee',
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem'
                }
              }}
              icon={<WarningIcon />}
            >
              <Typography variant="body1" fontWeight={500}>
                {errorMessage}
              </Typography>
            </Alert>
          )}

          {/* Modern Orders List */}
          {orders.length > 0 && (
            <Stack spacing={3}>
              {orders?.map((order, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: '20px',
                    border: '1px solid #e8e8e8',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                      borderColor: '#d0d0d0',
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Modern Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          sx={{
                            bgcolor: '#f0f7ff',
                            color: '#1976d2',
                            width: 48,
                            height: 48,
                          }}
                        >
                          <ReceiptIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="700" sx={{ color: '#1a1a1a' }}>
                            {order.order_code}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Order ID: #{order.order_id}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={order.active_status}
                        color={getStatusColor(order.active_status)}
                        size="medium"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          height: '32px',
                          borderRadius: '16px',
                        }}
                      />
                    </Box>

                    {/* Modern Details Grid */}
                    <Grid container spacing={3} mb={3}>
                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            bgcolor: '#f8fffe',
                            borderRadius: '16px',
                            border: '1px solid #e8f5e8',
                            height: '100%',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} mb={1}>
                            <Avatar sx={{ bgcolor: '#e8f5e8', color: '#2e7d32', width: 32, height: 32 }}>
                              <MoneyIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Total Amount
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="700" sx={{ color: '#2e7d32' }}>
                            N{order.total_price}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            bgcolor: '#fafafa',
                            borderRadius: '16px',
                            border: '1px solid #e8e8e8',
                            height: '100%',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} mb={1}>
                            <Avatar sx={{ bgcolor: '#e8e8e8', color: '#666', width: 32, height: 32 }}>
                              <CalendarIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Order Date
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="600" sx={{ color: '#1a1a1a' }}>
                            {formattedDate(order.order_date)}
                          </Typography>
                        </Paper>
                      </Grid>

                      {order.balance_amount !== null && (
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              bgcolor: '#fff8e1',
                              borderRadius: '16px',
                              border: '1px solid #ffe0b2',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={2} mb={1}>
                              <Avatar sx={{ bgcolor: '#ffe0b2', color: '#f57c00', width: 32, height: 32 }}>
                                <TimeIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                Balance Amount
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="700" sx={{ color: '#f57c00' }}>
                              N {order.balance_due ?? 0}
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>

                    {/* Reverse Code */}
                    {order.reversed_code?.reversed_code && (
                      <Box mb={3}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: '#fff3e0',
                            borderRadius: '12px',
                            border: '1px solid #ffcc02',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <WarningIcon sx={{ color: '#f57c00' }} />
                            <Typography variant="body1" fontWeight={600} sx={{ color: '#f57c00' }}>
                              Reverse Code: {order.reversed_code.reversed_code}
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Modern Action Button */}
                    <Box mt={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        size="large"
                        startIcon={<CancelIcon />}
                        onClick={() => confirmCancelOrder(order)}
                        disabled={isLoading}
                        sx={{
                          borderRadius: '16px',
                          py: 2,
                          fontWeight: 700,
                          fontSize: '1rem',
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: '0 8px 25px rgba(211, 47, 47, 0.3)',
                            transform: 'translateY(-2px)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          }
                        }}
                      >
                        Cancel Order
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {/* Modern No Results */}
          {searchQuery && !isLoading && orders.length === 0 && !errorMessage && (
            <Paper
              elevation={0}
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                borderRadius: '20px',
                border: '1px solid #e8e8e8',
                bgcolor: 'white',
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#f5f5f5',
                  color: '#999',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CartIcon sx={{ fontSize: '2rem' }} />
              </Avatar>
              <Typography variant="h5" fontWeight="600" sx={{ mb: 2, color: '#1a1a1a' }}>
                No Orders Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                We couldn't find any orders matching your search. Try searching with a different order number.
              </Typography>
            </Paper>
          )}

          {/* Modern Confirmation Dialog */}
          <Dialog
            open={showDialog}
            onClose={() => setShowDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { 
                borderRadius: '24px',
                p: 2,
              }
            }}
          >
            <DialogTitle sx={{ pb: 2, pt: 3 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#ffebee', color: '#d32f2f', width: 48, height: 48 }}>
                  <CancelIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="700">
                    Cancel Order
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    This action cannot be undone
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ py: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
                Are you sure you want to cancel order{' '}
                <Typography component="span" fontWeight="700" color="text.primary">
                  {selectedOrder?.order_code}
                </Typography>
                ? This will permanently cancel the order and cannot be reversed.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
              <Button
                onClick={() => setShowDialog(false)}
                variant="outlined"
                size="large"
                sx={{ 
                  borderRadius: '12px',
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Keep Order
              </Button>
              <Button
                onClick={handleReverseOrder}
                variant="contained"
                color="error"
                size="large"
                disabled={isLoading}
                sx={{ 
                  borderRadius: '12px',
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Cancel Order'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modern Snackbar */}
          <Snackbar
            open={showSnackbar}
            autoHideDuration={4000}
            onClose={() => setShowSnackbar(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setShowSnackbar(false)}
              severity={snackbarSeverity}
              variant="filled"
              sx={{ 
                borderRadius: '16px',
                fontWeight: 500,
                '& .MuiAlert-icon': {
                  fontSize: '1.25rem'
                }
              }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default ReversedOrder;